import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "sonner";

export type RoomType = "movie" | "game" | "cooking" | "music";
export type GameType = "trivia" | "wouldyourather" | "truth_or_dare" | "drawing" | "twenty_questions";

interface VirtualDateRoom {
  id: string;
  host_id: string;
  guest_id: string | null;
  room_type: RoomType;
  activity_state: Record<string, any>;
  status: "waiting" | "active" | "ended";
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
}

interface VirtualDateActivity {
  id: string;
  room_id: string;
  activity_type: string;
  question_data: Record<string, any> | null;
  user1_response: Record<string, any> | null;
  user2_response: Record<string, any> | null;
  is_complete: boolean;
  created_at: string;
}

interface PartnerProfile {
  id: string;
  name: string;
  photo_url: string | null;
}

// Game question banks
const WOULD_YOU_RATHER = [
  { optionA: "Travel to the past", optionB: "Travel to the future" },
  { optionA: "Have unlimited money", optionB: "Have unlimited time" },
  { optionA: "Live in a big city", optionB: "Live in a small town" },
  { optionA: "Be able to fly", optionB: "Be invisible" },
  { optionA: "Always be 10 minutes late", optionB: "Always be 20 minutes early" },
  { optionA: "Never use social media again", optionB: "Never watch a movie again" },
  { optionA: "Have a rewind button", optionB: "Have a pause button" },
  { optionA: "Read minds", optionB: "See the future" },
];

const TRIVIA_QUESTIONS = [
  { question: "What year did the first iPhone release?", answer: "2007", options: ["2005", "2006", "2007", "2008"] },
  { question: "What's the capital of Australia?", answer: "Canberra", options: ["Sydney", "Melbourne", "Canberra", "Brisbane"] },
  { question: "How many planets in our solar system?", answer: "8", options: ["7", "8", "9", "10"] },
  { question: "Who painted the Mona Lisa?", answer: "Leonardo da Vinci", options: ["Michelangelo", "Leonardo da Vinci", "Raphael", "Van Gogh"] },
  { question: "What's the largest ocean?", answer: "Pacific", options: ["Atlantic", "Indian", "Pacific", "Arctic"] },
];

const TRUTH_QUESTIONS = [
  "What's your most embarrassing dating story?",
  "What's something you've never told anyone?",
  "What's your biggest pet peeve?",
  "What's the craziest thing on your bucket list?",
  "What's your guilty pleasure?",
  "What's a deal-breaker for you in relationships?",
  "What's the most romantic thing you've ever done?",
  "What's your biggest fear?",
];

const DARE_PROMPTS = [
  "Do your best dance move",
  "Tell a joke",
  "Do an impression of a celebrity",
  "Show your most used emoji",
  "Sing the first song that comes to mind",
  "Make a funny face",
  "Tell a story using only hand gestures",
  "Give your best compliment",
];

export const useVirtualDate = (roomId?: string) => {
  const { user } = useAuthContext();
  const [room, setRoom] = useState<VirtualDateRoom | null>(null);
  const [activities, setActivities] = useState<VirtualDateActivity[]>([]);
  const [partner, setPartner] = useState<PartnerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentActivity, setCurrentActivity] = useState<VirtualDateActivity | null>(null);

  // Create a new virtual date room
  const createRoom = useCallback(
    async (guestId: string, type: RoomType = "game") => {
      if (!user) return null;

      const { data, error } = await supabase
        .from("virtual_date_rooms")
        .insert({
          host_id: user.id,
          guest_id: guestId,
          room_type: type,
          status: "waiting",
        })
        .select()
        .single();

      if (error) {
        toast.error("Failed to create date room");
        console.error(error);
        return null;
      }

      toast.success("Virtual date created!");
      return data;
    },
    [user]
  );

  // Join/start the room
  const startRoom = useCallback(async () => {
    if (!room || !user) return;

    const { error } = await supabase
      .from("virtual_date_rooms")
      .update({
        status: "active",
        started_at: new Date().toISOString(),
      })
      .eq("id", room.id);

    if (error) {
      toast.error("Failed to start date");
    }
  }, [room, user]);

  // End the room
  const endRoom = useCallback(async () => {
    if (!room) return;

    const { error } = await supabase
      .from("virtual_date_rooms")
      .update({
        status: "ended",
        ended_at: new Date().toISOString(),
      })
      .eq("id", room.id);

    if (error) {
      toast.error("Failed to end date");
    } else {
      toast.success("Thanks for the virtual date! 💕");
    }
  }, [room]);

  // Start a new game activity
  const startActivity = useCallback(
    async (gameType: GameType) => {
      if (!room || !user) return;

      let questionData: Record<string, any> = {};

      switch (gameType) {
        case "wouldyourather":
          questionData = WOULD_YOU_RATHER[Math.floor(Math.random() * WOULD_YOU_RATHER.length)];
          break;
        case "trivia":
          questionData = TRIVIA_QUESTIONS[Math.floor(Math.random() * TRIVIA_QUESTIONS.length)];
          break;
        case "truth_or_dare":
          questionData = {
            truth: TRUTH_QUESTIONS[Math.floor(Math.random() * TRUTH_QUESTIONS.length)],
            dare: DARE_PROMPTS[Math.floor(Math.random() * DARE_PROMPTS.length)],
          };
          break;
        case "twenty_questions":
          questionData = { currentQuestion: 1, maxQuestions: 20, guesses: [] };
          break;
        case "drawing":
          const prompts = ["cat", "house", "sunset", "pizza", "rocket", "flower", "guitar", "elephant"];
          questionData = { prompt: prompts[Math.floor(Math.random() * prompts.length)] };
          break;
      }

      const { data, error } = await supabase
        .from("virtual_date_activities")
        .insert({
          room_id: room.id,
          activity_type: gameType,
          question_data: questionData,
        })
        .select()
        .single();

      if (error) {
        toast.error("Failed to start activity");
        return null;
      }

      setCurrentActivity(data as unknown as VirtualDateActivity);
      return data as unknown as VirtualDateActivity;
    },
    [room, user]
  );

  // Submit response to activity
  const submitResponse = useCallback(
    async (activityId: string, response: Record<string, any>) => {
      if (!user || !room) return;

      const isHost = room.host_id === user.id;
      const updateField = isHost ? "user1_response" : "user2_response";

      const { error } = await supabase
        .from("virtual_date_activities")
        .update({ [updateField]: response })
        .eq("id", activityId);

      if (error) {
        toast.error("Failed to submit response");
      }
    },
    [user, room]
  );

  // Complete activity
  const completeActivity = useCallback(async (activityId: string) => {
    const { error } = await supabase
      .from("virtual_date_activities")
      .update({ is_complete: true })
      .eq("id", activityId);

    if (error) {
      console.error("Failed to complete activity:", error);
    }
  }, []);

  // Update shared state (for movie sync, etc.)
  const updateActivityState = useCallback(
    async (state: Record<string, any>) => {
      if (!room) return;

      const { error } = await supabase
        .from("virtual_date_rooms")
        .update({
          activity_state: {
            ...room.activity_state,
            ...state,
          },
        })
        .eq("id", room.id);

      if (error) {
        console.error("Failed to update state:", error);
      }
    },
    [room]
  );

  // Fetch room data
  useEffect(() => {
    if (!roomId || !user) {
      setLoading(false);
      return;
    }

    const fetchRoom = async () => {
      const { data: roomData, error } = await supabase
        .from("virtual_date_rooms")
        .select("*")
        .eq("id", roomId)
        .single();

      if (error || !roomData) {
        toast.error("Room not found");
        setLoading(false);
        return;
      }

      setRoom(roomData as VirtualDateRoom);

      // Fetch partner profile
      const partnerId = roomData.host_id === user.id ? roomData.guest_id : roomData.host_id;
      if (partnerId) {
        const { data: partnerData } = await supabase
          .from("profiles")
          .select("id, name, photo_url")
          .eq("id", partnerId)
          .single();

        if (partnerData) {
          setPartner(partnerData);
        }
      }

      // Fetch activities
      const { data: activitiesData } = await supabase
        .from("virtual_date_activities")
        .select("*")
        .eq("room_id", roomId)
        .order("created_at", { ascending: false });

      if (activitiesData) {
        setActivities(activitiesData as VirtualDateActivity[]);
        const active = activitiesData.find((a) => !a.is_complete);
        if (active) {
          setCurrentActivity(active as VirtualDateActivity);
        }
      }

      setLoading(false);
    };

    fetchRoom();

    // Subscribe to room changes
    const roomChannel = supabase
      .channel(`virtual-date-${roomId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "virtual_date_rooms", filter: `id=eq.${roomId}` },
        (payload) => {
          if (payload.new) {
            setRoom(payload.new as VirtualDateRoom);
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "virtual_date_activities", filter: `room_id=eq.${roomId}` },
        (payload) => {
          if (payload.eventType === "INSERT") {
            const newActivity = payload.new as unknown as VirtualDateActivity;
            setActivities((prev) => [newActivity, ...prev]);
            if (!newActivity.is_complete) {
              setCurrentActivity(newActivity);
            }
          } else if (payload.eventType === "UPDATE") {
            const updatedActivity = payload.new as unknown as VirtualDateActivity;
            setActivities((prev) =>
              prev.map((a) => (a.id === updatedActivity.id ? updatedActivity : a))
            );
            if (payload.new.id === currentActivity?.id) {
              setCurrentActivity(payload.new as VirtualDateActivity);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(roomChannel);
    };
  }, [roomId, user, currentActivity?.id]);

  return {
    room,
    partner,
    activities,
    currentActivity,
    loading,
    createRoom,
    startRoom,
    endRoom,
    startActivity,
    submitResponse,
    completeActivity,
    updateActivityState,
    isHost: room?.host_id === user?.id,
  };
};
